import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "@/components/vlk";
import { useAuth } from "@/context/AuthContext";

/**
 * Opens a WebSocket to `/api/ws/notifications` for the authenticated user,
 * shows a toast on every incoming notification, and invalidates React Query
 * caches so the notification center + KPIs refresh live.
 * Returns the unread count for use in the topbar bell.
 */
export function useRealtimeNotifications() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);

  const { data: unread } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: () => api.get("/notifications/unread-count").then(r => r.data),
    enabled: !!user,
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let warnedOnError = false;  // rate-limit console.warn to first occurrence per hook instance
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace(/^http/, "ws") + "/api/ws/notifications";

    const connect = () => {
      if (cancelled) return;
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { backoffRef.current = 1000; warnedOnError = false; };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.kind === "notification") {
          const n = msg.notification || {};
          const toneFn = ({ critical: toast.error, high: toast.error, medium: toast.warning }[n.level]) || toast.message || toast;
          try { toneFn(n.title, { description: n.body }); } catch { toast.message?.(n.title, { description: n.body }); }
          qc.invalidateQueries({ queryKey: ["notifications"] });
          qc.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
          qc.invalidateQueries({ queryKey: ["kpis"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (cancelled) return;
        const delay = Math.min(backoffRef.current, 30_000);
        backoffRef.current = Math.min(delay * 2, 30_000);
        setTimeout(connect, delay);
      };
      ws.onerror = (ev) => { if (!warnedOnError) { console.warn("[notifications] ws error, will reconnect", ev); warnedOnError = true; } try { ws.close(); } catch { /* close-on-error may already be closing */ } };
    };
    connect();
    return () => { cancelled = true; try { wsRef.current?.close(); } catch (e) { console.warn("[notifications] cleanup close failed", e); } };
  }, [user, qc]);

  return { unread: unread?.count ?? 0 };
}
